import React, { useCallback, useContext, useMemo, useState } from 'react';
import uniqueId from 'lodash/uniqueId';
import set from 'lodash/set';
import get from 'lodash/get';
import {
  FeedbackForm,
  FeedbackFormElementText,
  FeedbackFormElementTypeEnum,
  FeedbackFormElementMultiScaleStatement
} from '../../../../../../types';
import { FeedbackFormContextType } from './types';
import { createContext } from 'react';
import { feedbackFormHasErrors } from '../../../../../../utils/validation';
import { standaloneLessonsSelCompetencies } from '@src/constants';

const createNewId = () => uniqueId(`${Date.now().toString(16)}-feedback-form-`);

function noop() {
}

export const defaultFeedbackForm: FeedbackForm = {
  elements: [
    {
      id: createNewId(),
      type: FeedbackFormElementTypeEnum.text,
      value: 'What is your name?',
      isSensitive: true,
      askExplanation: false,
      competency: standaloneLessonsSelCompetencies[0].value,
    },
  ],
  doNotSendToTeacher: false,
  instructions: '',
  contentInstruction: '',
  finalCopy: 'Your answers have been sent to your teacher.',
  surveyType: '',
  learningOutcomes: ''
}

export const defaultFeedbackFormWithOutName: FeedbackForm = {
  elements: [],
  doNotSendToTeacher: false,
  instructions: '',
  contentInstruction: '',
  finalCopy: '',
  surveyType: '',
  learningOutcomes: ''
}

const FeedbackFormContext = createContext<FeedbackFormContextType>({
  ...defaultFeedbackForm,
  changeFormValue: noop,
  changeElementsValue: noop,
  onChangeChecks: noop,
  // @ts-ignore
  getFormState: noop,
  createQuestion: noop,
  removeQuestion: noop,
  addOption: noop,
  removeOption: noop,
  setForm: noop,
  clearOptions: noop,
});

type Props = {
  children: React.ReactNode;
}

export const FeedbackFormProvider = (props: Props) => {
  const [form, setForm] = useState<FeedbackForm>(defaultFeedbackForm);

  const changeFormValue = useCallback((value, path) => {
    set(form, path, value);

    setForm({
      ...form
    });
  }, [form])

  const changeElementsValue = useCallback((value, path) => {
    set(form.elements, path, value);

    setForm({
      ...form,
      elements: [...form.elements],
    });
  }, [form]);

  const getFormState = useCallback(() => {
    return form;
  }, [form]);

  const createQuestion = useCallback(() => {
    const newQuestion: FeedbackFormElementText = {
      id: createNewId(),
      type: FeedbackFormElementTypeEnum.text,
      value: '',
      placeholder: 'Question',
      competency: standaloneLessonsSelCompetencies[0].value,
    };

    setForm({
      ...form,
      elements: [...form.elements, newQuestion],
    });
  }, [form]);

  const removeQuestion = useCallback((index) => {
    form.elements.splice(index, 1);

    setForm({
      ...form,
      elements: [...form.elements],
    });
  }, [form]);

  const addOption = useCallback((parentIndex: number, value: string | FeedbackFormElementMultiScaleStatement, isStatement?: boolean) => {
    const path = `${parentIndex}.${isStatement ? 'statements' : 'options'}`;
    const options = get(form.elements, path, []) as any[];
    const assessments = get(form.elements, `${parentIndex}.assessments`) as Boolean[] | undefined;
    options.push(value);
    set(form.elements, path, options);
    if (assessments) {
      assessments.push(false);
      set(form.elements, `${parentIndex}.assessments`, assessments);
    }
    setForm({
      ...form,
      elements: [...form.elements],
    });
  }, [form]);

  const removeOption = useCallback((parentIndex: number, index: number, isStatement?: boolean) => {
    const path = `${parentIndex}.${isStatement ? 'statements' : 'options'}`;
    const options = get(form.elements, path, []) as string[];
    options.splice(index, 1);
    set(form.elements, path, options);
    const isAssessment = (get(form.elements, `${parentIndex}.type`, '') as string).includes('Assessable');
    if (isAssessment) {
      const assessments = get(form.elements, `${parentIndex}.assessments`) as Boolean[] | undefined;
      if (assessments) {
        assessments.splice(index, 1);
        set(form.elements, `${parentIndex}.assessments`, assessments);
      } else {
        set(form.elements, `${parentIndex}.assessments`, []);
      }
    }
    setForm({
      ...form,
      elements: [...form.elements],
    });
  }, [form]);

  const clearOptions = useCallback((parentIndex: number) => {
    set(form.elements, `${parentIndex}.options`, []);
    const assessments = get(form.elements, `${parentIndex}.assessments`);
    if (assessments) {
      set(form.elements, `${parentIndex}.assessments`, []);
    }
    setForm({
      ...form,
      elements: [...form.elements],
    });
  }, [form]);

  const onChangeChecks = useCallback((checkedList: boolean[], parentIndex: number) => {
    set(form.elements, `${parentIndex}.assessments`, checkedList);
    setForm({
      ...form,
      elements: [...form.elements],
    });
  }, [form]);

  const error = useMemo(() => {
    return feedbackFormHasErrors(form);
  }, [form]);

  return (
    <FeedbackFormContext.Provider
      value={{
        ...form,
        onChangeChecks,
        changeFormValue,
        changeElementsValue,
        getFormState,
        createQuestion,
        removeQuestion,
        addOption,
        removeOption,
        setForm,
        clearOptions,
        hasError: error,
      }}>
      {props.children}
    </FeedbackFormContext.Provider>
  )
}

export const useFeedbackForm = () => useContext(FeedbackFormContext);

export const ValidationErrors = {
  empty: 'Please fill in the field.',
}
